import React from 'react';

import { A11YLiveMessage } from '@lumapps/aria-live/components/A11YLiveMessage';
import { useDataAttributes } from '@lumapps/data-attributes';
import { mdiChevronDown, mdiChevronUp } from '@lumapps/lumx/icons';
import { FlexBox, FlexBoxProps, Link, LinkProps, Orientation, Size, Typography } from '@lumapps/lumx/react';
import { GLOBAL, useTranslate } from '@lumapps/translations';

export interface DisclosedListProps extends Pick<FlexBoxProps, 'className' | 'gap' | 'orientation'> {
    /** Whether the UI should be displayed as expanded */
    isExpanded: boolean;
    /** Callback when the expanded state toggles */
    toggleExpanded: () => void;
    /** Whether the more button should be displayed */
    hasMore: boolean;
    /** Children to display as list above the more button */
    children: React.ReactNode;
    /** Props to be forwarded to the content element */
    contentProps?: Partial<FlexBoxProps>;
    /** Props to be forwarded to the "more" button */
    toggleButtonProps?: Partial<LinkProps>;
    /** Label of the "more" button */
    toggleButtonLabel?: string;
    /** Scope of the component, for tracking purposes. */
    scope?: string;
}

/**
 * UI component of the Disclosed List
 * This component's state is completely controllable and lets you set any component as children.
 *
 * The children are wrapped inside an aria-live section to read when new values are displayed.
 */
export const DisclosedList = ({
    children,
    isExpanded,
    toggleExpanded,
    hasMore,
    contentProps,
    toggleButtonProps,
    toggleButtonLabel,
    scope = 'disclosed-list',
    ...rest
}: DisclosedListProps) => {
    const { translateKey } = useTranslate();
    const { get } = useDataAttributes(scope);

    const defaultToggleButtonLabel = translateKey(isExpanded ? GLOBAL.LESS : GLOBAL.MORE);

    return (
        <FlexBox orientation={Orientation.vertical} gap={Size.big} {...get({ element: 'wrapper' })} {...rest}>
            <FlexBox
                orientation={rest.orientation || Orientation.vertical}
                aria-live="polite"
                aria-relevant="additions"
                {...contentProps}
            >
                {children}
            </FlexBox>
            <A11YLiveMessage hidden>{!isExpanded && hasMore && translateKey(GLOBAL.CONTENT_HIDDEN)}</A11YLiveMessage>
            {hasMore && (
                /**
                 * Ideally, the "more" button should have a "disclosure" aria pattern and
                 * be between the first values and the ones displayed by toggling the state.
                 * This would allow a clear navigation to screenReaders + keyboard users as
                 * now more information would be displayed beneath the button when toggled.
                 *
                 * However, since we want to keep this component as flexible as possible, we instead keep a
                 * default button without any aria role, and let the `aria-live` section read-out the newly displayed values.
                 *
                 */
                // The DS actually renders a button here. Disable the rule.
                // eslint-disable-next-line jsx-a11y/anchor-is-valid
                <Link
                    leftIcon={isExpanded ? mdiChevronUp : mdiChevronDown}
                    typography={Typography.body1}
                    onClick={toggleExpanded}
                    {...toggleButtonProps}
                    {...get({ element: 'toggle', action: isExpanded ? 'less' : 'more' })}
                >
                    {toggleButtonLabel ?? defaultToggleButtonLabel}
                </Link>
            )}
        </FlexBox>
    );
};
